"use client";

import { useState } from "react";
import type { CatalogPractice } from "@/lib/mock-catalog";

type SubmissionFormProps = {
  moduleSlug: string;
  practice: CatalogPractice;
};

type ReviewResponse = {
  ok: boolean;
  persisted: boolean;
  submissionId: string;
  review: {
    status: "approved" | "revision" | "pending";
    score: number;
    summary: string;
    issues: string[];
    nextSteps: string[];
    assumptions: string[];
    criteria: Array<{
      title: string;
      score: number;
      passed: boolean;
      feedback: string;
    }>;
    model: string;
  };
  files: {
    archiveStored: boolean;
    pdfStored: boolean;
  };
  system: {
    mode: string;
    mysql: {
      enabled: boolean;
      host: string | null;
      database: string | null;
    };
    openai: {
      enabled: boolean;
      reviewModel: string;
      imageModel: string;
    };
  };
};

type ErrorResponse = {
  error?: string;
};

export function SubmissionForm({ moduleSlug, practice }: SubmissionFormProps) {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">(
    "idle",
  );
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ReviewResponse | null>(null);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("loading");
    setError(null);
    setResult(null);

    const formData = new FormData(event.currentTarget);
    formData.set("moduleSlug", moduleSlug);
    formData.set("practiceSlug", practice.slug);

    const response = await fetch("/api/submissions", {
      method: "POST",
      body: formData,
    });

    const data = (await response.json()) as ReviewResponse | ErrorResponse;

    if (!response.ok) {
      setStatus("error");
      setError(
        "error" in data ? data.error || "No se pudo enviar la entrega." : "No se pudo enviar la entrega.",
      );
      return;
    }

    setResult(data as ReviewResponse);
    setStatus("success");
    event.currentTarget.reset();
  }

  return (
    <div className="panel-strong p-7">
      <p className="eyebrow">Entrega real</p>
      <h2 className="mt-4 text-3xl font-semibold text-white">
        Sube una práctica y recibe corrección automática.
      </h2>
      <p className="mt-4 text-sm leading-7 text-slate-300">
        Esta primera versión acepta repositorio, archivo comprimido y PDF de
        apoyo. Si MySQL está configurado, los archivos se guardan y la
        evaluación se persiste.
      </p>

      <form onSubmit={handleSubmit} className="mt-8 space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <label className="block space-y-2">
            <span className="text-sm text-slate-300">Nombre</span>
            <input
              name="submittedByName"
              type="text"
              className="w-full rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-teal-300/40"
              placeholder="Alumno de prueba"
            />
          </label>
          <label className="block space-y-2">
            <span className="text-sm text-slate-300">Email</span>
            <input
              name="submittedByEmail"
              type="email"
              className="w-full rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-teal-300/40"
              placeholder="alumno@tusclasesonline.com"
            />
          </label>
        </div>

        <label className="block space-y-2">
          <span className="text-sm text-slate-300">Repositorio</span>
          <input
            name="repositoryUrl"
            type="url"
            className="w-full rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-teal-300/40"
            placeholder="https://github.com/usuario/proyecto"
          />
        </label>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="block space-y-2">
            <span className="text-sm text-slate-300">ZIP del proyecto</span>
            <input
              name="archiveFile"
              type="file"
              accept=".zip,.tar,.gz,.rar"
              className="w-full rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-slate-300 outline-none transition file:mr-4 file:rounded-full file:border-0 file:bg-white file:px-4 file:py-2 file:text-sm file:font-semibold file:text-slate-950 focus:border-teal-300/40"
            />
          </label>
          <label className="block space-y-2">
            <span className="text-sm text-slate-300">PDF de apoyo</span>
            <input
              name="pdfFile"
              type="file"
              accept=".pdf"
              className="w-full rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-slate-300 outline-none transition file:mr-4 file:rounded-full file:border-0 file:bg-white file:px-4 file:py-2 file:text-sm file:font-semibold file:text-slate-950 focus:border-teal-300/40"
            />
          </label>
        </div>

        <label className="block space-y-2">
          <span className="text-sm text-slate-300">Notas para la revisión</span>
          <textarea
            name="notes"
            rows={5}
            className="w-full rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-teal-300/40"
            placeholder="Explica qué funciona, qué falta y en qué quieres que se fije el sistema."
          />
        </label>

        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "loading" ? "Corrigiendo..." : "Enviar práctica"}
        </button>
      </form>

      {error ? (
        <div className="mt-6 rounded-3xl border border-rose-300/16 bg-rose-300/10 p-4 text-sm leading-6 text-rose-100">
          {error}
        </div>
      ) : null}

      {result ? (
        <div className="mt-8 space-y-5">
          <div className="rounded-3xl border border-white/8 bg-white/4 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-slate-400">Resultado</p>
                <h3 className="mt-2 text-2xl font-semibold text-white">
                  {result.review.score}/100
                </h3>
              </div>
              <div className="rounded-full border border-teal-300/20 bg-teal-300/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-teal-100">
                {result.review.status}
              </div>
            </div>
            <p className="mt-4 text-sm leading-7 text-slate-300">
              {result.review.summary}
            </p>
            <p className="mt-4 text-xs uppercase tracking-[0.18em] text-slate-500">
              Modelo: {result.review.model}
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-3xl border border-white/8 bg-white/4 p-5">
              <p className="text-sm text-slate-400">Issues</p>
              <div className="mt-4 space-y-3">
                {result.review.issues.map((issue) => (
                  <div key={issue} className="text-sm leading-6 text-slate-200">
                    {issue}
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-white/8 bg-white/4 p-5">
              <p className="text-sm text-slate-400">Siguientes pasos</p>
              <div className="mt-4 space-y-3">
                {result.review.nextSteps.map((step) => (
                  <div key={step} className="text-sm leading-6 text-slate-200">
                    {step}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-white/8 bg-white/4 p-5">
            <p className="text-sm text-slate-400">Criterios</p>
            <div className="mt-4 grid gap-3">
              {result.review.criteria.map((criterion) => (
                <div
                  key={criterion.title}
                  className="rounded-2xl border border-white/8 bg-slate-950/40 px-4 py-4"
                >
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="text-sm font-semibold text-white">
                      {criterion.title}
                    </h4>
                    <span className="font-mono text-sm text-teal-200">
                      {criterion.score}
                    </span>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-300">
                    {criterion.feedback}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-white/8 bg-white/4 p-5 text-sm leading-6 text-slate-300">
            Persistencia: {result.persisted ? "guardada en MySQL" : "modo local"}.
            Archivos: ZIP {result.files.archiveStored ? "guardado" : "no guardado"}; PDF{" "}
            {result.files.pdfStored ? "guardado" : "no guardado"}.
          </div>
        </div>
      ) : null}
    </div>
  );
}
